import * as RadixTabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import React from 'react';
import { TextColor } from '../../../types';
import { Icons } from '../Icons';
import { Typography, TypographyType } from '../Typography';
import { Tab, TabsType } from './Tabs.types';

interface TabsTriggerProps {
  tab: Tab;
  type: TabsType;
  setActiveTab: (newActiveTab: string) => void;
  activeTab?: string;
}

const TabsTrigger = ({ activeTab, tab, type, setActiveTab }: TabsTriggerProps) => {
  const { activeColor, id, title, disabled = false, icon } = tab;

  const isActive = activeTab === id;

  const activeColorClassName = type === TabsType.BOXED ? TextColor.PRIMARY : activeColor || 'text-primary';
  const colorClassName = isActive ? activeColorClassName : TextColor.PRIMARY;

  const getBoxedTriggerStyles = () => {
    let classes = 'rounded-[4px]';
    if (isActive) classes += ' bg-gray-900/20';
    return classes;
  };

  const renderTrigger = () => {
    const textType = type === TabsType.BORDERED ? TypographyType.SMALL : TypographyType.BASE;
    const textProps = { type: textType, color: colorClassName, noWrap: true };
    return (
      <RadixTabs.Trigger
        key={id}
        value={id}
        className={clsx(
          'flex cursor-pointer items-center justify-center gap-2 px-4',
          disabled && 'pointer-events-none opacity-40',
          type === TabsType.BOXED && getBoxedTriggerStyles()
        )}
        onClick={() => setActiveTab(id)} // We call setActiveTab here and not in onValueChange because we want to always click the tab even if it is already active.
      >
        {icon && <Icons icon={icon} color={textProps.color} />}
        {typeof title === 'string' && <Typography {...textProps}>{title}</Typography>}
        {typeof title === 'object' && React.cloneElement(title, { ...textProps, ...title.props })}
      </RadixTabs.Trigger>
    );
  };

  if (type === TabsType.BORDERED) {
    return (
      <div
        className={clsx(
          'flex h-8 items-center border-b-2',
          isActive ? `${colorClassName} border-b-current` : 'border-b-base-300'
        )}
      >
        {renderTrigger()}
      </div>
    );
  }

  return renderTrigger();
};

export default TabsTrigger;
